import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  beginTelegramRealtimeConnection,
  getPortfolioThresholdSettings,
  getTelegramRealtimeStatus,
  setTelegramRealtimeActive,
  type TelegramRealtimeStatus,
} from '../../api/portfolioAlerts';
import {
  enableWebPush,
  getWebPushBrowserStatus,
  getWebPushServerStatus,
  syncExistingWebPushSubscription,
  type WebPushBrowserStatus,
  type WebPushServerStatus,
} from '../live/webPushClient';

type Props = {
  open: boolean;
  userId: number | null;
  onClose: () => void;
  onOpenSettings: () => void;
};

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const EMPTY_SERVER: WebPushServerStatus = {
  serverConfigured: false,
  vapidPublicKey: '',
  subscribed: false,
  subscriptionCount: 0,
};

const EMPTY_BROWSER: WebPushBrowserStatus = {
  supported: false,
  permission: 'unsupported',
  subscribed: false,
};

function snoozeKey(userId: number) {
  return `policyintel:notification-onboarding:snooze:v1:${userId}`;
}

function handledSessionKey(userId: number) {
  return `policyintel:notification-onboarding:handled:v1:${userId}`;
}

export default function NotificationOnboardingDialog({
  open,
  userId,
  onClose,
  onOpenSettings,
}: Props) {
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [holdingCount, setHoldingCount] = useState(0);
  const [server, setServer] = useState<WebPushServerStatus>(EMPTY_SERVER);
  const [browser, setBrowser] = useState<WebPushBrowserStatus>(EMPTY_BROWSER);
  const [telegram, setTelegram] = useState<TelegramRealtimeStatus | null>(null);
  const [telegramDeepLink, setTelegramDeepLink] = useState('');
  const [message, setMessage] = useState('');

  const pushReady = browser.supported
    && browser.permission === 'granted'
    && browser.subscribed
    && server.serverConfigured
    && server.subscribed
    && server.subscriptionCount > 0;
  const telegramReady = Boolean(telegram?.linked && telegram?.realtimeAlertsActive);

  const loadEligibility = useCallback(async () => {
    if (!open || !userId || userId <= 0) return;

    try {
      if (sessionStorage.getItem(handledSessionKey(userId)) === '1') {
        setEligible(false);
        onClose();
        return;
      }
    } catch {
      // Restricted storage must not break onboarding.
    }

    try {
      const snoozedAt = Number(localStorage.getItem(snoozeKey(userId)) || 0);
      if (Number.isFinite(snoozedAt) && snoozedAt > 0 && Date.now() - snoozedAt < SNOOZE_MS) {
        setEligible(false);
        onClose();
        return;
      }
    } catch {
      // Storage restrictions must not break login.
    }

    setLoading(true);
    setMessage('');
    try {
      const [settings, initialServer, initialBrowser, telegramStatus] = await Promise.all([
        getPortfolioThresholdSettings(),
        getWebPushServerStatus(),
        getWebPushBrowserStatus(),
        getTelegramRealtimeStatus().catch(() => null),
      ]);

      let authoritativeServer = initialServer;
      if (
        initialServer.serverConfigured
        && initialBrowser.permission === 'granted'
        && initialBrowser.subscribed
        && !initialServer.subscribed
      ) {
        await syncExistingWebPushSubscription();
        authoritativeServer = await getWebPushServerStatus();
      }

      setHoldingCount(settings.holdingCount);
      setServer(authoritativeServer);
      setBrowser(initialBrowser);
      setTelegram(telegramStatus);

      const webPushOn = initialBrowser.supported
        && initialBrowser.permission === 'granted'
        && initialBrowser.subscribed
        && authoritativeServer.serverConfigured
        && authoritativeServer.subscribed
        && authoritativeServer.subscriptionCount > 0;
      const telegramOn = Boolean(telegramStatus?.linked && telegramStatus?.realtimeAlertsActive);

      // Prompt only when the user actually has holdings and no realtime notification channel is active.
      const shouldPrompt = settings.holdingCount > 0 && !webPushOn && !telegramOn;
      setEligible(shouldPrompt);
      if (!shouldPrompt) onClose();
    } catch {
      // Onboarding must never block the normal post-login experience.
      setEligible(false);
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose, open, userId]);

  useEffect(() => {
    void loadEligibility();
  }, [loadEligibility]);

  const markHandledForSession = useCallback(() => {
    if (!userId) return;
    try {
      sessionStorage.setItem(handledSessionKey(userId), '1');
    } catch {
      // Restricted storage must not block the user action.
    }
  }, [userId]);

  const handleLater = useCallback(() => {
    markHandledForSession();

    if (userId) {
      try {
        localStorage.setItem(snoozeKey(userId), String(Date.now()));
      } catch {
        // ignore
      }
    }

    setEligible(false);
    onClose();
  }, [markHandledForSession, onClose, userId]);

  const handleOpenSettings = useCallback(() => {
    markHandledForSession();
    setEligible(false);
    onOpenSettings();
  }, [markHandledForSession, onOpenSettings]);

  const handleActivate = useCallback(async () => {
    setActivating(true);
    setMessage('');
    let pushActivated = pushReady;
    let telegramActivated = telegramReady;

    try {
      if (!pushReady && server.serverConfigured && browser.supported && browser.permission !== 'denied') {
        await enableWebPush(server.vapidPublicKey);
        const [nextServer, nextBrowser] = await Promise.all([
          getWebPushServerStatus(),
          getWebPushBrowserStatus(),
        ]);
        setServer(nextServer);
        setBrowser(nextBrowser);
        pushActivated = nextBrowser.supported
          && nextBrowser.permission === 'granted'
          && nextBrowser.subscribed
          && nextServer.serverConfigured
          && nextServer.subscribed
          && nextServer.subscriptionCount > 0;
      }

      if (!telegramReady && telegram?.linked) {
        const nextTelegram = await setTelegramRealtimeActive(true);
        setTelegram(nextTelegram);
        telegramActivated = Boolean(nextTelegram?.linked && nextTelegram?.realtimeAlertsActive);
      }

      if (!telegramReady && !telegram?.linked) {
        // Telegram cannot be fully enabled without the provider's required /start confirmation.
        // Start the secure one-time connection flow, but do not pretend it is already active.
        try {
          const next = await beginTelegramRealtimeConnection();
          setTelegram(next);
          setTelegramDeepLink(String(next?.deepLink ?? '').trim());
        } catch {
          // WebPush may still have been activated successfully.
        }
      }

      if (pushActivated || telegramActivated) {
        markHandledForSession();
        setEligible(false);
        onClose();
        return;
      } else if (browser.permission === 'denied') {
        setMessage('브라우저 알림이 차단되어 있습니다. 사이트 설정에서 알림 권한을 허용하거나 Telegram을 연결해 주세요.');
      } else {
        setMessage('수신 채널을 활성화하지 못했습니다. 마이페이지 → 알림에서 상태를 확인해 주세요.');
      }
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '알림 활성화 중 오류가 발생했습니다.');
    } finally {
      setActivating(false);
    }
  }, [
    browser,
    markHandledForSession,
    onClose,
    pushReady,
    server,
    telegram,
    telegramReady,
  ]);

  const statusText = useMemo(() => {
    if (pushReady || telegramReady) return '알림 수신 준비 완료';
    return `보유종목 ${holdingCount}개를 감시할 수 있습니다`;
  }, [holdingCount, pushReady, telegramReady]);

  if (!open || (!eligible && !loading)) return null;

  return (
    <Dialog open={open && (eligible || loading)} onClose={handleLater} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 950, pb: 0.6 }}>보유종목 변화를 놓치지 않도록 알림을 받을까요?</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ minHeight: 120, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography sx={{ color: '#596678', fontSize: 12.4, lineHeight: 1.65 }}>
              시장 전체 알림이 아니라 포트폴리오에 등록한 보유종목만 감시합니다. 종목이 사용자가 설정한 당일 상승·하락 기준에 처음 도달하면 Web Push 또는 Telegram으로 알려드립니다.
            </Typography>

            <Stack direction="row" spacing={0.6} sx={{ mt: 1.1, flexWrap: 'wrap', gap: 0.6 }}>
              <Chip size="small" label={statusText} sx={{ fontWeight: 900 }} />
              <Chip size="small" label={`웹푸시 ${pushReady ? 'ON' : 'OFF'}`} sx={{ fontWeight: 900 }} />
              <Chip size="small" label={`Telegram ${telegramReady ? 'ON' : 'OFF'}`} sx={{ fontWeight: 900 }} />
            </Stack>

            {browser.permission === 'denied' ? (
              <Typography sx={{ mt: 1, color: '#a06a0a', fontSize: 11.2, lineHeight: 1.55 }}>
                이 브라우저는 알림 권한이 차단되어 있어 자동으로 다시 켤 수 없습니다. 브라우저 사이트 설정에서 허용하거나 Telegram을 사용해 주세요.
              </Typography>
            ) : null}

            {telegramDeepLink ? (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: '#596678', fontSize: 11.2, lineHeight: 1.55 }}>
                  Web Push는 켤 수 있지만 Telegram은 최초 1회 보안 연결이 필요합니다. 아래 버튼으로 Telegram을 열고 Start를 한 번 눌러 주세요.
                </Typography>
                <Button
                  component="a"
                  href={telegramDeepLink}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.7, fontWeight: 900, textTransform: 'none' }}
                >
                  Telegram에서 Start
                </Button>
              </Box>
            ) : null}

            {message ? (
              <Typography sx={{ mt: 1, color: '#334155', fontSize: 11.4, lineHeight: 1.55 }}>
                {message}
              </Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2, flexWrap: 'wrap' }}>
        <Button onClick={handleLater} disabled={activating} sx={{ fontWeight: 850, textTransform: 'none' }}>나중에</Button>
        <Button onClick={handleOpenSettings} disabled={activating} sx={{ fontWeight: 850, textTransform: 'none' }}>알림 설정</Button>
        <Button variant="contained" onClick={() => void handleActivate()} disabled={loading || activating} sx={{ fontWeight: 950, textTransform: 'none' }}>
          {activating ? '활성화 중...' : '알림 켜기'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
